import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tooltip, Typography, Spin, Input } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { usePagination } from '../context/PaginationProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../util/axiosConfig';
import '../styles/userlist.css';

const { Search } = Input;
const { Text } = Typography;

const UserList: React.FC = () => {
    const { pagination, setTotal, setPage, setPageSize } = usePagination();
    const { page, pageSize, sort_by, sort_order } = pagination;
    const [users, setUsers] = useState<any>([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [searchKey, setSearchKey] = useState('');
    const [paginationConfig, setPaginationConfig] = useState({
        current: page,
        pageSize: pageSize,
        total: 0,
        showTotal: (total: number, range: [number, number]) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50"],
        locale: { items_per_page: "" },
        onChange: (page: number, pageSize: number) => {
            setPage(page);
            setPageSize(pageSize);
            setPaginationConfig((prev: any) => ({
                ...prev,
                current: page,
                pageSize: pageSize,
            }));
        },
        onShowSizeChange: (current: number, size: number) => {
            setPage(current);
            setPageSize(size);
            setPaginationConfig((prev: any) => ({
                ...prev,
                current: current,
                pageSize: size,
            }));
        },
    });

    const fetchUserList = async () => {
        setLoading(true);
        try {
            const response = await axios.post('/api/v1/user/pageable-user', {}, {
                params: {
                    pageSize: pageSize,
                    pageNo: page - 1,
                    sortByField: sort_by,
                    sortDirection: 'desc',
                    search: searchKey
                },
            });
            if (Array.isArray(response?.data?.data?.content)) {
                setUsers(response?.data?.data?.content);
                setTotal(response.data?.data?.totalElements || 0);
                setPaginationConfig((prev) => ({
                    ...prev,
                    total: response.data?.data?.totalElements || 0,
                }));
            } else {
                console.error('Unexpected response format:', response.data);
            }
        } catch (error) {
            console.error('Failed to fetch panel data', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserList();
        // eslint-disable-next-line
    }, [page, pageSize, sort_by, sort_order, searchKey]);

    const handleSearch = (value: string) => {
        setSearchKey(value);
    };

    const handleEdit = (id: any, record: any) => {
        navigate(`/user-list/edit/${id}`);
    };

    const handleAdd = () => {
        navigate(`/user-list/add`);
    };

    const columns: any = [
        {
            title: 'Name',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'User Role',
            dataIndex: 'singleRoleDtoList',
            key: 'singleRoleDtoList',
        },
        // {
        //     title: 'Plant',
        //     dataIndex: 'plant',
        //     key: 'plant',
        //     render: (plantList: any[]) => {
        //         if (!Array.isArray(plantList) || plantList.length === 0) {
        //             return '-';
        //         }
        //         const firstPlant = plantList[0]?.name;
        //         const otherPlants = plantList.slice(1).map((plant: any) => (
        //             <div key={plant.name}>{plant.name}</div>
        //         ));
        //         const count = plantList.length > 1 ? ` +${plantList.length - 1}` : '';
        //         return (
        //             <Tooltip title={otherPlants.length ? <>{otherPlants}</> : undefined}>
        //                 {firstPlant}<Text type="secondary">{count}</Text>
        //             </Tooltip>
        //         );
        //     }
        // },
        {
            title: 'Doc Type',
            dataIndex: 'poDocumentType',
            key: 'poDocumentType',
            render: (docTypeList: any[]) => {
                if (!Array.isArray(docTypeList) || docTypeList.length === 0) {
                    return '-';
                }
                const firstDocType = docTypeList[0]?.docType;
                const otherDocTypes = docTypeList.slice(1).map((docType: any) => (
                    <div key={docType.docType}>{docType.docType}</div>
                ));
                const count = docTypeList.length > 1 ? ` +${docTypeList.length - 1}` : '';
                return (
                    <Tooltip title={otherDocTypes.length ? <>{otherDocTypes}</> : undefined}>
                        {firstDocType}<Text type="secondary">{count}</Text>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Purchase Group',
            dataIndex: 'purchaseGroup',
            key: 'purchaseGroup',
            render: (purchaseGroupList: any[]) => {
                if (!Array.isArray(purchaseGroupList) || purchaseGroupList.length === 0) {
                    return '-';
                }
                const firstGroup = purchaseGroupList[0]?.name;
                const otherGroups = purchaseGroupList.slice(1).map((group: any) => (
                    <div key={group.name}>{group.name}</div>
                ));
                const count = purchaseGroupList.length > 1 ? ` +${purchaseGroupList.length - 1}` : '';
                return (
                    <Tooltip title={otherGroups.length ? <>{otherGroups}</> : undefined}>
                        {firstGroup}<Text type="secondary">{count}</Text>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Status',
            key: 'active',
            dataIndex: 'active',
            render: (active: boolean) => (
                <span style={{ color: active ? '#00BC40' : '#CF1919' }}>
                    {active ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (text: any, record: any) => (
                <>
                    <EditOutlined
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleEdit(record.id, record)}
                    />
                </>
            ),
        },
    ];


    return (
        <>
            <Card
                title="Masters - User List"
                bordered={false}
                extra={
                    <>
                        <Search
                            placeholder="Search..."
                            className="mt-2"
                            allowClear
                            onSearch={handleSearch}
                            style={{
                                width: 250,
                                marginRight: 10,
                            }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            Add User
                        </Button>
                    </>
                }
            >{loading ? (
                <div className="spin-container">
                    <Spin size='default' /><span style={{ marginLeft: '7px' }}>Loading...</span>
                </div>
            ) : (<Table
                className='userTable'
                dataSource={users}
                size='small'
                columns={columns}
                bordered={false}
                pagination={paginationConfig}
            />)}
            </Card>
        </>
    );
};

export default UserList;
